import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { SnackBarService } from './snack-bar.service';
import { environment } from 'src/environments/environment';
import { catchError, map, Observable, throwError } from 'rxjs';
import { SdlIdName } from '@shared/models/sdlIdName.model';
import { GENERAL_ERROR_MESSAGE } from '@shared/models/messages';
import { OTHER_COMPANY_ID, OTHER_PROJECT_ID } from '@shared/constants';
import { Country } from '@shared/models/country.model';
import { SdlIdMdmIdName } from '@shared/models/SdlIdMdmIdName.model';

@Injectable({
  providedIn: 'root',
})
export class DictionaryDataApiService {
  readonly http = inject(HttpClient);
  private readonly _messageService = inject(SnackBarService);
  readonly url = `${environment.apiUrl}`;

  readonly OTHER_COMPANY_ID = OTHER_COMPANY_ID;
  readonly OTHER_PROJECT_ID = OTHER_PROJECT_ID;
  readonly OTHER = 'Other';

  getCompanies$(sectorId?: number): Observable<SdlIdName[]> {
    const filter = sectorId ? `?$filter=industrySegments eq ${sectorId}` : '';

    return this.http.get<SdlIdName[]>(`${this.url}/companies${filter}`).pipe(
      map(companies => this.sortOtherToTop(companies)),
      catchError(error => {
        this._messageService.showError(`Failed to retrieve companies: ${error.error.message ?? GENERAL_ERROR_MESSAGE}.`);
        return throwError(() => error);
      })
    );
  }

  getProjects$(countries: SdlIdName[]): Observable<SdlIdName[]> {
    const params = { countryIds: countries.map(country => country.sdlId).join(',') };
    return this.http.get<SdlIdName[]>(`${this.url}/v2/projects`, { params }).pipe(
      map(projects => this.sortOtherToTop(projects)),
      catchError(error => {
        this._messageService.showError(`Failed to retrieve projects: ${error.error.message ?? GENERAL_ERROR_MESSAGE}.`);
        return throwError(() => error);
      })
    );
  }

  getSectors$(): Observable<SdlIdMdmIdName[]> {
    return this.http.get<SdlIdMdmIdName[]>(`${this.url}/industry-segments`).pipe(
      map(sectors => this.sortOtherToTop(sectors)),
      catchError(error => {
        this._messageService.showError(`Failed to retrieve sectors: ${error.error.message ?? GENERAL_ERROR_MESSAGE}.`);
        return throwError(() => error);
      })
    );
  }

  private sortOtherToTop(values: SdlIdName[]) {
    return values.sort((a, b) => {
      if (a.name.startsWith(this.OTHER)) return -1;
      if (b.name.startsWith(this.OTHER)) return 1;
      return a.name.localeCompare(b.name);
    });
  }

  getCountries$(): Observable<Country[]> {
    return this.http.get<Country[]>(`${this.url}/countries`).pipe(
      map(countries => this.sortCountries(countries)),
      catchError(error => {
        this._messageService.showError(`Failed to retrieve countries:  ${error.error.message ?? GENERAL_ERROR_MESSAGE}.`);
        return throwError(() => error);
      })
    );
  }

  private sortCountries(countries: Country[]): Country[] {
    return countries.sort((a, b) => a.name.localeCompare(b.name));
  }

  getBusinessUnits$(): Observable<string[]> {
    return this.http.get<string[]>(`${this.url}/organizational-units/business-units`).pipe(
      catchError(error => {
        this._messageService.showError(`Failed to retrieve business units: ${error.error.message ?? GENERAL_ERROR_MESSAGE}.`);
        return throwError(() => error);
      })
    );
  }

  getLegalEntities$(): Observable<string[]> {
    return this.http.get<string[]>(`${this.url}/organizational-units/legal-entities`).pipe(
      catchError(error => {
        this._messageService.showError(`Failed to retrieve legal entities: ${error.error.message ?? GENERAL_ERROR_MESSAGE}.`);
        return throwError(() => error);
      })
    );
  }
}
